import {Button} from "primereact/button";
import React, {useEffect, useState} from "react";
import {useUpdatePriorityTaskStatusMutation, useUpdatePriorityTaskValueMutation, useUpdatePriorityTaskQuestionsMutation} from "../../apis/priorityApi";
import {useUpdateKpiTaskStatusMutation, useUpdateKpiTaskValueMutation, useUpdateKpiTaskQuestionsMutation} from "../../apis/kpiApi";
import {useUpdateSAMTaskStatusMutation, useUpdateSAMTaskValueMutation, useUpdateSAMTaskQuestionsMutation} from "../../apis/samApi";
import {Tooltip} from "primereact/tooltip";
import {useNavigate} from "react-router-dom";
import {useUserProfileQuery} from "../../apis/authenticationApi";
import {useSubTaskTree, useResponsibleHiearchicalList, useAllTasksUnder} from "../../hooks/taskHooks";
import TaskTable from "../../components/tables/TaskTable";
import NeedHelpDialog from "./NeedHelpDialog";

const TaskComponent = ({task}) => {
    const [updatePriorityTaskStatus] = useUpdatePriorityTaskStatusMutation();
    const [updatePriorityTaskValue] = useUpdatePriorityTaskValueMutation();
    const [updatePriorityTaskQuestions] = useUpdatePriorityTaskQuestionsMutation();
    const [updateKpiTargetStatus] = useUpdateKpiTaskStatusMutation();
    const [updateKpiTargetValue] = useUpdateKpiTaskValueMutation();
    const [updateKpiTargetQuestions] = useUpdateKpiTaskQuestionsMutation();
    const [updateSAMTaskStatus] = useUpdateSAMTaskStatusMutation();
    const [updateSAMTaskValue] = useUpdateSAMTaskValueMutation();
    const [updateSAMTaskQuestions] = useUpdateSAMTaskQuestionsMutation();
    const [completeBtnEnabled, setCompleteBtnEnabled] = useState(false);
    const [subTaskCounter, setSubTaskCounter] = useState("0/0");
    const {data: userProfile} = useUserProfileQuery();
    const hierarchyUsers = useResponsibleHiearchicalList(userProfile?.company?.account);
    const inHiearchyOfUser = hierarchyUsers?.find(u => u?.id === task?.responsible);
    const [expandSubTasks, setExpandSubTasks] = useState(false);
    const [needHelpVisible, setNeedHelpVisible] = useState(false);

    const navigate = useNavigate();

    const taskDetail = useSubTaskTree(task);
    const allTasksUnder = useAllTasksUnder(task, true);

    const changeStatus = (status) => {
        if(task.taskType === 'Priority') {
            updatePriorityTaskStatus({id: task.id, status});
        }
        else if(task.taskType === 'KPI') {
            updateKpiTargetStatus({id: task.id, status});
        }
        else if(task.taskType === 'SAM') {
            updateSAMTaskStatus({id: task.id, status});
        }
    }

    const changeValue = (value) => {
        if(task.taskType === 'Priority') {
            updatePriorityTaskValue({id: task.id, completionValue: Math.min(Math.max(value, 0), 100)});
        }
        else if(task.taskType === 'KPI') {
            updateKpiTargetValue({id: task.id, completionValue: Math.min(Math.max(value, 0), 100)});
        }
        else if(task.taskType === 'SAM') {
            updateSAMTaskValue({id: task.id, completionValue: Math.min(Math.max(value, 0), 100)});
        }
    }


    const changeNeedHelpQuestions = (questions) => {
        if(task.taskType === 'Priority') {
            updatePriorityTaskQuestions({id: task.id, needHelpQuestionList: questions});
        }
        else if(task.taskType === 'KPI') {
           updateKpiTargetQuestions({id: task.id, needHelpQuestionList: questions});
        }
        else if(task.taskType === 'SAM') {
           updateSAMTaskQuestions({id: task.id, needHelpQuestionList: questions});
        }
    }

    const todoButtonPanel = () => {
        return (<>
            <Button type="button" label="New Task (Click to Accept)" className="p-button-text" onClick={() => changeStatus("Accepted")} /></>);
    }

    const acceptedButtonPanel = () => {
        return (<>
            <Button type="button" label="Need Help" className="p-button-text" onClick={() => setNeedHelpVisible(true)} />
            <Button type="button" label="Start Doing" className="p-button-text" onClick={() => changeStatus("Doing")} /></>);
    }

    useEffect(() => {
       const completeEnabled = taskDetail && !(taskDetail?.children || []).find(c => !c?.completed);
       setCompleteBtnEnabled(completeEnabled);

       const completedTasksCnt = (taskDetail?.children || []).filter(e => e.completed).length;
       const completedSubTaskStr = taskDetail?.children?.length ? `${completedTasksCnt}/${taskDetail?.children.length}` : "0/0"
       setSubTaskCounter(completedSubTaskStr);

      }, [taskDetail]);
    
    
    const doingButtonPanel = () => {
        return (<>
            <Button type="button" label="Need Help" className="p-button-text" onClick={() => setNeedHelpVisible(true)} />
            <Button type="button" disabled={!completeBtnEnabled} label="Completed" className="p-button-text" onClick={() => changeStatus("Done")} />
            <div className="value-buttons">
                <div>%</div>
                <Button type="button" label="+" className="p-button-text" onClick={() => changeValue(task.completionValue + 25)} />
                <Button type="button" label="-" className="p-button-text" onClick={() => changeValue(task.completionValue - 25)} />
            </div></>);
    }

    const statusClass = (task.completionValue + 10) < task.calculatedPercent ? 'status-overdue' :
        (task.completionValue - 10) > task.calculatedPercent ? 'status-great' : 'status-ok';

    const deadlineText =
        Date.now() > task.deadlineMillis ? "Task deadline has passed" :
        task.accepted === '-' && (task.createdMillis + 259200000) < Date.now() ? "Task has not been accepted" :
        task.started === '-' && (task.acceptedMillis + 604800000) < Date.now() ? "Task has not been started" :
        statusClass === 'status-overdue' ? "Task is behind schedule" : undefined;

    const createSubTask = () => navigate(`${subTaskMethod(task)}/${task?.id}`);

    return (
        <>
            <Tooltip target=".tooltip" />
            <NeedHelpDialog visible={needHelpVisible} task={task} onHide={() => setNeedHelpVisible(false)} onUpdateQuestionList={changeNeedHelpQuestions} needHelpQuestionList={(task.needHelpQuestionList || []).sort((a, b) => b.createdAt - a.createdAt)}/>
            <div className="task-card">
                <div className="task-card-header">
                    <div className={`task-header-title type-${task.taskType.toLowerCase()}`}>{task.taskType.toUpperCase()}: {task.parentName}</div>
                    <div className="task-header-progress">{task.completionValue}%</div>
                </div>
                <div className="task-card-body">
                    <div className="task-body-details">
                        <div className="task-details-description">{task.name}</div>
                        <div className="task-details-type">{task.taskType} Task</div>
                        <div className={`task-details-deadline ${statusClass}`}>{deadlineText ? (<i className="fa fa-triangle-exclamation tooltip" data-pr-position="left" data-pr-tooltip={deadlineText} /> ) : ""}Deadline: {task.deadline}</div>
                        <div className="task-details-created">Created: {task.created}</div>
                        { task.accepted !== "-" && (<div className="task-details-accepted">Accepted: {task.accepted}</div>)}
                        { task.started !== "-" && (<div className="task-details-started">Started: {task.started}</div>)}
                        { task.completed !== "-" && (<div className="task-details-completed">Completed: {task.completed}</div>)}
                        { subTaskCounter &&  (<div className="task-details-sub-task"><div style={{display: "inline-block"}}>Sub-tasks: {subTaskCounter}</div>{inHiearchyOfUser && <Button label="" icon="pi pi-plus" onClick={createSubTask} />}</div>)}
                        { subTaskCounter && 
                            <>
                                <Button label={expandSubTasks? "Hide Sub-tasks" : "Expand Sub-tasks"} onClick={() => setExpandSubTasks(!expandSubTasks)}  icon={expandSubTasks? "pi pi-caret-down" : "pi pi-caret-right"} iconPos="right" link />
                                { expandSubTasks &&
                                    <TaskTable tasks={allTasksUnder} newSubTaskEndpoint={subTaskMethod} editTaskEndpoint={['CompanyOwner','CompanyAdmin', 'CompanyManager'].includes(userProfile?.company?.role)? editTaskMethod : undefined} hideDeadline={true} hideSubtaskStatus={true}/>
                                }
                            </>
                        }
                    </div>
                    <div className="task-body-progressbar">
                        <div className={`task-progressbar-value status ${statusClass}`} style={{height: `${task.completionValue}%`}} />
                        <div className="task-progressbar-line" style={{height: `${task.calculatedPercent}%`}}></div>
                    </div>
                </div>
                <div className="task-card-footer">
                    { task.taskStatus === 'ToDo' && task.accepted === '-' && todoButtonPanel()}
                    { task.taskStatus === 'ToDo' && task.accepted !== '-' && acceptedButtonPanel()}
                    { task.taskStatus === 'Doing' && doingButtonPanel()}
                </div>
            </div>
        </>
    )
}


export const subTaskMethod = ({parent, taskType}) => {
    let uri = "";
    if(taskType === 'SAM') {
        uri = "sam-sub-task";
    } else if (taskType === 'Priority') {
        uri = "priority-sub-task";
    } else if (taskType === 'KPI') {
        uri = "kpi-sub-task";
    }

    return `${uri}/${parent}/task/new/parent-task`;
}


export const editTaskMethod = ({taskType}) => {
    let uri = "";
    if(taskType === 'SAM') {
        uri = "sam-edit-task";
    } else if (taskType === 'Priority') {
        uri = "priority-edit-task";
    } else if (taskType === 'KPI') {
        uri = "kpi-edit-task";
    }

    return uri;
}

export default TaskComponent;
